import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLabels, useRequestData, usePrefs, useFlags } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import {
	HighlightedAttribute,
	isValidFuelType,
	getFuelTypeIconAndPopoverLabel
} from 'wsm-highlighted-attribute';
import { VehicleViews } from 'wsm-vehicle-views';
import { ErrorBoundary } from 'wsm-error-boundary';
import { getVehicleTitle, remapPrefs } from '../utils/utility';
import { displayAttrsList } from '../global-constants';

const VehicleTitle = ({ isInSidebar = false, viewsError, vehicleViews }) => {
	const labels = useLabels();
	const { deviceType } = useRequestData();
	const flags = useFlags();
	const unmappedPrefs = usePrefs();
	const prefs = isInSidebar
		? remapPrefs(unmappedPrefs, '-sidebar')
		: unmappedPrefs;
	// ^ This is bad and should probably be removed. I doubt it's necessary to be doing this mapping of prefs to change classnames for the sidebar.
	// I would consider refactoring this and adjusting styles in the design system to achieve the desired effect
	// instead of this obtuse trick.
	const { title, vehicle } = useSelector((state) => state);
	const showVINandStock = flags['ws-vehicle-title-show-stock-and-vin'];
	const displaySeoSubTitle = vehicle.seoSubTitle.length;
	const SeoSubTitleLine = displaySeoSubTitle ? (
		<span
			className={setClassNames([
				'additional-details',
				'd-block',
				'mt-0',
				'mb-1',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor
			])}
		>
			{vehicle.seoSubTitle}
		</span>
	) : null;

	const displayAdditionalDetails =
		displayAttrsList.includes('vin') &&
		displayAttrsList.includes('stockNumber');

	const shouldShowHighlightedFuelType =
		vehicle.normalFuelType &&
		isValidFuelType(vehicle.normalFuelType) &&
		prefs.enableFuelTypeHighlight === 'true';

	let fuelTypeIcon;
	let fuelTypePopover;
	if (shouldShowHighlightedFuelType) {
		[fuelTypeIcon, fuelTypePopover] = getFuelTypeIconAndPopoverLabel({
			fuelType: vehicle.normalFuelType,
			electricIcon: prefs.highlightedFuelTypeElectricIcon,
			hybridIcon: prefs.highlightedFuelTypeHybridIcon,
			labels
		});
	}

	const isVehicleViewsEnabled =
		prefs.enableVehicleViews === 'true' &&
		vehicleViews >= prefs.vehicleViews_minimumViews;

	const additionalDetails = displayAdditionalDetails ? (
		// ATTN: We need spaces between <li>'s
		// This fixes a strange double click selection issue in browsers
		<ul
			className={setClassNames([
				'additional-details',
				'list-inline',
				'mb-0',
				'font-weight-normal',
				prefs.titleDetailsSize,
				prefs.titleDetailsColor,
				prefs.enableFuelTypeHighlight ? 'mt-3' : ''
			])}
		>
			{shouldShowHighlightedFuelType && (
				<li>
					<ErrorBoundary newRelicPageAction="ws-vehicle-title HighlightedAttribute error boundary">
						<HighlightedAttribute
							icon={fuelTypeIcon}
							popoverContent={fuelTypePopover}
							type="fuelType"
							chipClasses={[
								vehicle.normalFuelType.toLowerCase(),
								'mr-3',
								isVehicleViewsEnabled && 'mb-3'
							]}
						>
							{vehicle.normalFuelType}
						</HighlightedAttribute>
					</ErrorBoundary>
				</li>
			)}
			{isVehicleViewsEnabled && !viewsError && (
				<li>
					<ErrorBoundary newRelicPageAction="ws-vehicle-title VehicleViews error boundary">
						<VehicleViews
							vehicleViews={vehicleViews}
							disclaimerLabel={prefs.vehicleViews_disclaimerLabel}
							displayStyle={prefs.vehicleViews_displayStyle}
							icon={prefs.vehicleViews_icon}
							iconPopular={prefs.vehicleViews_iconPopular}
							minimumViews={prefs.vehicleViews_minimumViews}
							timeFrame={prefs.vehicleViews_timeFrame}
						/>
					</ErrorBoundary>
				</li>
			)}
			{vehicle.vin && showVINandStock && (
				<li className="align-text-bottom">
					{`${labels.get('VIN')}: `}
					{vehicle.vin}{' '}
				</li>
			)}
			{vehicle.stockNumber && showVINandStock && (
				<li className="align-text-bottom">
					{` ${labels.get('STOCK')}: `}
					{vehicle.stockNumber}{' '}
				</li>
			)}
			{/* This may be added back in at some point */}
			{/* {modelCode && <li> {labels.get('MODEL')}: {modelCode} </li>} */}
		</ul>
	) : null;

	const vehicleTitle = getVehicleTitle(
		title,
		labels,
		vehicle.type,
		vehicle.certified,
		prefs.forceShowVehicleCondition
	);

	const decodeMarkup = (encodedTitle) => {
		return { __html: `${encodedTitle}` };
	};

	const vehicleMarkup = (
		<div>
			<h1
				className={setClassNames([
					'vehicle-title',
					'm-0',
					'line-height-reset',
					deviceType !== 'DESKTOP' && 'h2'
				])}
			>
				<span
					className={setClassNames([
						'd-block',
						prefs.titleHeading,
						prefs.titleHeadingColor,
						prefs.titleHeadingWeight,
						prefs.titleHeadingSize
					])}
				>
					{vehicleTitle.length > 1 && vehicleTitle[0]}
				</span>
				<span
					className={setClassNames([
						prefs.titleHeadingColor,
						prefs.titleHeadingTagLineWeight,
						prefs.titleHeadingTagLineSize
					])}
					dangerouslySetInnerHTML={decodeMarkup(
						vehicleTitle.length > 1
							? vehicleTitle[1]
							: vehicleTitle[0]
					)}
				/>
				{SeoSubTitleLine}
			</h1>
			{additionalDetails}
		</div>
	);

	if (Object.keys(title).length === 0 && title.constructor === Object) {
		return null;
	}

	return vehicleMarkup;
};

VehicleTitle.propTypes = {
	isInSidebar: PropTypes.bool,
	viewsError: PropTypes.bool.isRequired,
	vehicleViews: PropTypes.shape({}).isRequired
};

VehicleTitle.defaultProps = {
	isInSidebar: false
};

export default VehicleTitle;
